import React from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { getDomain } from "@/theme/domains";

/* ─── LABEL ─────────────────────────────────────────────────────────────────── */

function FieldLabel({ children }) {
  if (!children) return null;
  return (
    <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">
      {children}
    </span>
  );
}

/* ─── INPUT ──────────────────────────────────────────────────────────────────── */

export function Input({ label, className = "", testId, ...props }) {
  return (
    <label className="block space-y-1.5" data-testid={testId ? `${testId}-wrap` : undefined}>
      <FieldLabel>{label}</FieldLabel>
      <input
        className={`
          w-full rounded-icon border border-slate-200/80 bg-white dark:bg-slate-800 dark:border-slate-700
          px-4 py-3 text-[16px] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500
          shadow-sm outline-none transition-all duration-200 ease-out
          focus:border-brand/50 focus:ring-4 focus:ring-brand/10 dark:focus:border-blue-400/50 dark:focus:ring-blue-400/10
          ${className}
        `}
        data-testid={testId}
        {...props}
      />
    </label>
  );
}

/* ─── TEXTAREA ───────────────────────────────────────────────────────────────── */

export function Textarea({ label, className = "", testId, ...props }) {
  return (
    <label className="block space-y-1.5">
      <FieldLabel>{label}</FieldLabel>
      <textarea
        rows={3}
        className={`
          w-full rounded-icon border border-slate-200/80 bg-white dark:bg-slate-800 dark:border-slate-700
          px-4 py-3 text-[16px] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500
          shadow-sm outline-none resize-none transition-all duration-200 ease-out
          focus:border-brand/50 focus:ring-4 focus:ring-brand/10 dark:focus:border-blue-400/50 dark:focus:ring-blue-400/10
          ${className}
        `}
        data-testid={testId}
        {...props}
      />
    </label>
  );
}

/* ─── BTN (base — all variants) ──────────────────────────────────────────────── */

export function Btn({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  iconLeft: IconLeft,
  iconRight: IconRight,
  className = "",
  children,
  testId,
  type = "button",
  ...props
}) {
  const base = `
    inline-flex items-center justify-center gap-2
    rounded-card font-semibold tracking-tight
    transition-all duration-200 ease-out
    active:scale-[0.98]
    disabled:opacity-50 disabled:pointer-events-none
    ${fullWidth ? "w-full" : ""}
  `;

  const sizes = {
    sm: "h-9  px-4 text-[13px]",
    md: "h-11 px-5 text-[15px]",
    lg: "h-14 px-6 text-[15px]",
  };

  const variants = {
    primary: "bg-brand text-white shadow-blue dark:shadow-none hover:brightness-105 active:opacity-80",
    secondary: "border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 hover:shadow-md",
    ghost: "bg-transparent text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800",
    danger: "border border-red-200/60 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 shadow-sm",
    success: "border border-emerald-200/70 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 shadow-sm",
  };

  return (
    <button
      type={type}
      disabled={loading || props.disabled}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
      data-testid={testId}
      {...props}
    >
      {loading ? (
        <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
      ) : (
        <>
          {IconLeft && <IconLeft size={size === "sm" ? 13 : 15} />}
          {children}
          {IconRight && <IconRight size={size === "sm" ? 13 : 15} />}
        </>
      )}
    </button>
  );
}

/* ─── NAMED ALIASES ──────────────────────────────────────────────────────────── */

export const PrimaryButton    = (props) => <Btn variant="primary"  {...props} />;
export const SecondaryButton  = (props) => <Btn variant="secondary" {...props} />;
export const GhostButton      = (props) => <Btn variant="ghost"    {...props} />;
export const TonalDangerButton   = (props) => <Btn variant="danger"  {...props} />;
export const TonalSuccessButton  = (props) => <Btn variant="success" {...props} />;

/* ─── INLINE ICON BUTTON ─────────────────────────────────────────────────────── */

// Tonal "hero-matched" treatment for TopBar action buttons.
// Each key matches the dominant accent of a page's Hero gradient.
// accent= takes priority over domain= for visual treatment.
const TOPBAR_ACCENT_MAP = {
  navy:    "bg-[#1b4f9c]/8 dark:bg-[#1b4f9c]/15 border border-[#1b4f9c]/20 dark:border-blue-500/30 text-[#1b4f9c] dark:text-blue-300 shadow-sm hover:bg-[#1b4f9c]/12 dark:hover:bg-[#1b4f9c]/22 hover:shadow-md",
  emerald: "bg-emerald-700/8 dark:bg-emerald-600/15 border border-emerald-600/20 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400 shadow-sm hover:bg-emerald-700/12 dark:hover:bg-emerald-600/22 hover:shadow-md",
  teal:    "bg-teal-700/8 dark:bg-teal-600/15 border border-teal-600/20 dark:border-teal-500/30 text-teal-700 dark:text-teal-400 shadow-sm hover:bg-teal-700/12 dark:hover:bg-teal-600/22 hover:shadow-md",
  violet:  "bg-violet-600/8 dark:bg-violet-500/15 border border-violet-500/20 dark:border-violet-400/30 text-violet-600 dark:text-violet-400 shadow-sm hover:bg-violet-600/12 dark:hover:bg-violet-500/22 hover:shadow-md",
  purple:  "bg-purple-700/8 dark:bg-purple-600/15 border border-purple-600/20 dark:border-purple-500/30 text-purple-700 dark:text-purple-400 shadow-sm hover:bg-purple-700/12 dark:hover:bg-purple-600/22 hover:shadow-md",
  indigo:  "bg-indigo-700/8 dark:bg-indigo-600/15 border border-indigo-600/20 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-400 shadow-sm hover:bg-indigo-700/12 dark:hover:bg-indigo-600/22 hover:shadow-md",
  rose:    "bg-rose-600/8 dark:bg-rose-500/15 border border-rose-500/20 dark:border-rose-400/30 text-rose-600 dark:text-rose-400 shadow-sm hover:bg-rose-600/12 dark:hover:bg-rose-500/22 hover:shadow-md",
  amber:   "bg-amber-700/8 dark:bg-amber-600/15 border border-amber-600/20 dark:border-amber-500/30 text-amber-700 dark:text-amber-400 shadow-sm hover:bg-amber-700/12 dark:hover:bg-amber-600/22 hover:shadow-md",
  slate:   "bg-slate-500/8 dark:bg-slate-400/10 border border-slate-400/20 dark:border-slate-500/25 text-slate-600 dark:text-slate-400 shadow-sm hover:bg-slate-500/12 dark:hover:bg-slate-400/15 hover:shadow-md",
};

export function InlineIconButton({
  icon: Icon,
  onClick,
  to,
  "aria-label": label,
  variant = "default",
  domain,
  accent,
  size = "md",
  className = "",
  iconClassName = "",
  ...rest
}) {
  const sizes = { sm: "h-9 w-9", md: "h-11 w-11" };
  const iconSizes = { sm: 14, md: 16 };
  const variants = {
    default: "bg-slate-800 dark:bg-slate-700 border border-slate-700/50 dark:border-slate-600/50 shadow-sm text-white hover:bg-slate-700 dark:hover:bg-slate-600 hover:shadow-md",
    brand:   "bg-brand text-white shadow-blue dark:shadow-none hover:brightness-105",
    ghost:   "text-slate-500 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-700/80 hover:text-slate-700 dark:hover:text-slate-200",
  };
  const variantCls = accent
    ? (TOPBAR_ACCENT_MAP[accent] ?? TOPBAR_ACCENT_MAP.slate)
    : domain
      ? `${getDomain(domain).pill} shadow-sm hover:brightness-105`
      : (variants[variant] ?? variants.default);
  const cls = `${sizes[size]} rounded-full flex items-center justify-center transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80 ${variantCls} ${className}`;

  if (to) {
    return <Link to={to} className={cls} aria-label={label} {...rest}>
      <Icon size={iconSizes[size]} className={iconClassName || undefined} />
    </Link>;
  }
  return (
    <button type="button" onClick={onClick} className={cls} aria-label={label} {...rest}>
      <Icon size={iconSizes[size]} className={iconClassName || undefined} />
    </button>
  );
}

/* ─── FAB BUTTON ─────────────────────────────────────────────────────────────── */

export function FABButton({
  icon: Icon = Plus,
  onClick,
  to,
  "aria-label": label = "Acción",
  domain,
  className = "",
}) {
  const domainTokens = domain ? getDomain(domain) : null;
  const domainCls = domainTokens
    ? (domainTokens.fabGlass ?? domainTokens.fab)
    : "bg-brand shadow-fab";
  const iconCls = domainTokens?.fabGlass ? domainTokens.iconColor : "text-white";
  const cls = `fixed right-5 z-[55] h-14 w-14 rounded-full ${domainCls} flex items-center justify-center transition-all duration-200 ease-out active:scale-[0.96] ${className}`;
  const style = { bottom: "calc(112px + env(safe-area-inset-bottom))" };

  if (to) {
    return <Link to={to} className={cls} style={style} aria-label={label}><Icon size={22} className={iconCls} /></Link>;
  }
  return (
    <button type="button" onClick={onClick} className={cls} style={style} aria-label={label}>
      <Icon size={22} className={iconCls} />
    </button>
  );
}

/* ─── LIST CARD ──────────────────────────────────────────────────────────────── */

export function ListCard({ to, onClick, children, className = "" }) {
  const cls = `bg-white dark:bg-slate-800 p-4 rounded-list border border-slate-200/50 dark:border-slate-700/50 shadow-card transition-all duration-300 ease-out hover:shadow-card-hover active:scale-[0.98] active:opacity-80 ${className}`;
  if (to) return <Link to={to} className={`block ${cls}`}>{children}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={`w-full text-left ${cls}`}>{children}</button>;
  return <div className={cls}>{children}</div>;
}

/* ─── CARD ───────────────────────────────────────────────────────────────────── */

export function Card({ children, className = "", onClick, testId }) {
  return (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden rounded-card border border-slate-200/70 dark:border-slate-700/60
        bg-white dark:bg-slate-800 p-4 shadow-card transition-all duration-200 ease-out
        ${onClick ? "cursor-pointer hover:shadow-card-hover active:scale-[0.98]" : ""}
        ${className}
      `}
      data-testid={testId}
    >
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white to-slate-50/40 dark:from-slate-800 dark:to-slate-800/30" />
      <div className="relative z-10">{children}</div>
    </div>
  );
}
